import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Tag, Gift, Check, X } from 'lucide-react';
import api from '../utils/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { trackBeginCheckout, trackAddPaymentInfo, trackPurchase } from '../utils/analytics';

export default function CheckoutPage() {
  const [searchParams] = useSearchParams();
  const [address, setAddress] = useState({
    name: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    pincode: ''
  });
  const [billingAddress, setBillingAddress] = useState({
    name: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    pincode: ''
  });
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);
  const [guestInfo, setGuestInfo] = useState({
    email: '',
    name: '',
    phone: ''
  });
  const [paymentConfig, setPaymentConfig] = useState({
    active_gateway: 'razorpay',
    razorpay: { enabled: false, key: '' },
    phonepe: { enabled: false, merchant_id: '' }
  });
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [rewardPoints, setRewardPoints] = useState({ points: 0, settings: null });
  const [pointsToUse, setPointsToUse] = useState(0);
  const [pointsDiscount, setPointsDiscount] = useState(0);
  const [processingPayment, setProcessingPayment] = useState(false);
  const { cart, getTotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const isGuest = !user;

  const handlePhonePeCallback = useCallback(async (orderId) => {
    try {
      setProcessingPayment(true);
      const orderResponse = await api.get(`/orders/${orderId}`);
      const order = orderResponse.data;
      
      if (order.phonepe_transaction_id) {
        const statusResponse = await api.get(`/phonepe/status/${order.phonepe_transaction_id}`);
        if (statusResponse.data.success && statusResponse.data.status === 'completed') {
          toast.success('Payment successful!');
          trackPurchase(order);
          clearCart();
          if (isGuest) {
            navigate(`/track-order?id=${orderId}`);
          } else {
            navigate('/dashboard');
          }
        } else if (statusResponse.data.status === 'pending') {
          toast.info('Payment is being processed. Please wait...');
        } else {
          toast.error(statusResponse.data.message || 'Payment failed');
        }
      }
    } catch (error) {
      toast.error('Error verifying payment status');
    } finally {
      setProcessingPayment(false);
    }
  }, [isGuest, navigate, clearCart]);

  const fetchSavedAddresses = useCallback(async () => {
    try {
      const response = await api.get('/user/addresses');
      if (response.data.shipping_address) {
        setAddress(response.data.shipping_address);
      }
      if (response.data.billing_address) {
        setBillingAddress(response.data.billing_address);
      }
      if (response.data.billing_same_as_shipping !== undefined) {
        setBillingSameAsShipping(response.data.billing_same_as_shipping);
      }
    } catch (error) {
    }
  }, []);

  const fetchPaymentConfig = useCallback(async () => {
    try {
      const response = await api.get('/payment-gateway-config');
      setPaymentConfig(response.data);
    } catch (error) {
      try {
        const razorpayResponse = await api.get('/razorpay-key');
        setPaymentConfig({
          active_gateway: 'razorpay',
          razorpay: { enabled: true, key: razorpayResponse.data.key },
          phonepe: { enabled: false, merchant_id: '' }
        });
      } catch (e) {
      }
    }
  }, []);

  const fetchRewardPoints = useCallback(async () => {
    try {
      const response = await api.get('/user/reward-points');
      setRewardPoints(response.data);
    } catch (error) {
    }
  }, []);

  useEffect(() => {
    const status = searchParams.get('status');
    const orderId = searchParams.get('order_id');
    if (status === 'callback' && orderId) {
      handlePhonePeCallback(orderId);
    }
  }, [searchParams, handlePhonePeCallback]);

  useEffect(() => {
    if (cart.length === 0 && !searchParams.get('order_id')) {
      navigate('/cart');
    } else if (cart.length > 0) {
      trackBeginCheckout(cart, getTotal());
    }
    fetchPaymentConfig();
    if (user) {
      fetchRewardPoints();
      fetchSavedAddresses();
    }
  }, [user, cart, searchParams, navigate, getTotal, fetchPaymentConfig, fetchRewardPoints, fetchSavedAddresses]);

  const applyPromoCode = async () => {
    if (!promoCode.trim()) {
      toast.error('Please enter a promo code');
      return;
    }

    try {
      const response = await api.post('/promo-codes/validate', {
        code: promoCode,
        order_amount: getTotal()
      });
      
      setPromoDiscount(response.data.discount);
      setAppliedPromo(response.data);
      toast.success(`Promo code applied! You save ₹${response.data.discount.toFixed(2)}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid promo code');
      setPromoDiscount(0);
      setAppliedPromo(null);
    }
  };

  const removePromoCode = () => {
    setPromoCode('');
    setPromoDiscount(0);
    setAppliedPromo(null);
    toast.info('Promo code removed');
  };

  const handlePointsChange = (points) => {
    const maxPoints = rewardPoints.points;
    const settings = rewardPoints.settings;
    
    if (!settings?.is_enabled) return;
    
    points = Math.min(points, maxPoints);
    points = Math.max(0, points);
    
    if (points > 0 && points < settings.min_points_to_redeem) {
      setPointsToUse(0);
      setPointsDiscount(0);
      return;
    }
    
    const subtotal = getTotal() - promoDiscount;
    const maxRedeem = (subtotal * settings.max_redeem_percent) / 100;
    let discount = points * settings.points_value_in_rupees;
    
    if (discount > maxRedeem) {
      discount = maxRedeem;
      points = Math.floor(discount / settings.points_value_in_rupees);
    }
    
    setPointsToUse(points);
    setPointsDiscount(discount);
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const getFinalTotal = () => {
    return Math.max(0, getTotal() - promoDiscount - pointsDiscount);
  };

  const handlePayment = async (e) => {
    e.preventDefault();

    // Validate guest info if not logged in
    if (isGuest) {
      if (!guestInfo.email || !guestInfo.name || !guestInfo.phone) {
        toast.error('Please fill in all contact information');
        return;
      }
    }

    const items = cart.map(item => ({
      product_id: item.product.id,
      product_name: item.product.name,
      quantity: item.quantity,
      base_price: parseFloat(item.price.basePrice),
      making_charges: parseFloat(item.price.makingCharges),
      gst_amount: parseFloat(item.price.gstAmount),
      total_price: parseFloat(item.price.totalPrice),
      image: item.product.images?.[0] || null  // Include product image for email notifications
    }));

    try {
      setProcessingPayment(true);
      let orderResponse;
      
      if (isGuest) {
        // Guest checkout
        orderResponse = await api.post('/orders/guest', {
          items,
          total_amount: getTotal(),
          shipping_address: address,
          billing_address: billingSameAsShipping ? address : billingAddress,
          billing_same_as_shipping: billingSameAsShipping,
          promo_code: appliedPromo?.code || null,
          guest_email: guestInfo.email,
          guest_name: guestInfo.name,
          guest_phone: guestInfo.phone
        });
      } else {
        // Logged in user checkout
        orderResponse = await api.post('/orders', {
          items,
          total_amount: getTotal(),
          shipping_address: address,
          billing_address: billingSameAsShipping ? address : billingAddress,
          billing_same_as_shipping: billingSameAsShipping,
          promo_code: appliedPromo?.code || null,
          points_to_use: pointsToUse
        });
      }

      const order = orderResponse.data;

      // If total is 0 (fully discounted), skip payment
      if (order.total_amount === 0) {
        toast.success('Order placed successfully!');
        // Track free order as purchase
        trackPurchase(order);
        clearCart();
        if (isGuest) {
          navigate(`/track-order?id=${order.id}`);
        } else {
          navigate('/dashboard');
        }
        return;
      }

      // Track payment info added
      trackAddPaymentInfo(paymentConfig.active_gateway, order.total_amount);

      // Determine which payment gateway to use
      const activeGateway = paymentConfig.active_gateway;

      if (activeGateway === 'phonepe' && paymentConfig.phonepe.enabled) {
        // PhonePe Payment
        await handlePhonePePayment(order);
      } else if (paymentConfig.razorpay.enabled && paymentConfig.razorpay.key) {
        // Razorpay Payment
        await handleRazorpayPayment(order);
      } else {
        toast.error('No payment gateway configured. Please contact support.');
      }
    } catch (error) {
      toast.error('Failed to create order');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handlePhonePePayment = async (order) => {
    try {
      const callbackUrl = `${window.location.origin}/checkout?order_id=${order.id}&status=callback`;
      
      const response = await api.post('/phonepe/initiate', {
        order_id: order.id,
        callback_url: callbackUrl
      });

      if (response.data.success && response.data.redirect_url) {
        // Redirect to PhonePe payment page
        window.location.href = response.data.redirect_url;
      } else {
        toast.error(response.data.message || 'Failed to initiate PhonePe payment');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate PhonePe payment');
    }
  };

  const handleRazorpayPayment = async (order) => {
    if (!order.razorpay_order_id) {
      toast.success('Order placed successfully!');
      clearCart();
      if (isGuest) {
        navigate(`/track-order?id=${order.id}`);
      } else {
        navigate('/dashboard');
      }
      return;
    }

    const res = await loadRazorpayScript();
    if (!res) {
      toast.error('Razorpay SDK failed to load');
      return;
    }

    const options = {
      key: paymentConfig.razorpay.key,
      amount: order.total_amount * 100,
      currency: 'INR',
      name: 'Marissa Gold And Diamonds',
      description: 'Jewelry Purchase',
      order_id: order.razorpay_order_id,
      handler: async function (response) {
        try {
          await api.post('/orders/verify-payment', {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature
          });
          toast.success('Payment successful!');
          // Track successful purchase
          trackPurchase(order);
          clearCart();
          if (isGuest) {
            navigate(`/track-order?id=${order.id}`);
          } else {
            navigate('/dashboard');
          }
        } catch (error) {
          toast.error('Payment verification failed');
        }
      },
      prefill: {
        name: isGuest ? guestInfo.name : user.name,
        email: isGuest ? guestInfo.email : user.email,
        contact: address.phone
      },
      theme: {
        color: '#1a3a5c'
      }
    };

    const paymentObject = new window.Razorpay(options);
    paymentObject.open();
  };

  return (
    <div className="min-h-screen py-12" data-testid="checkout-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-[#1a3a5c] mb-2" data-testid="checkout-title">
            Checkout
          </h1>
          <p className="text-gray-600">Complete your order</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <form onSubmit={handlePayment} className="bg-white p-8 rounded-lg border border-gray-200 space-y-6">
              
              {/* Guest Contact Information */}
              {isGuest && (
                <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 mb-6">
                  <h2 className="font-serif text-xl font-semibold text-[#1a3a5c] mb-4">Contact Information</h2>
                  <p className="text-sm text-amber-700 mb-4">Please provide your contact details for order updates</p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                      <input
                        type="email"
                        value={guestInfo.email}
                        onChange={(e) => setGuestInfo({ ...guestInfo, email: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] transition-colors"
                        placeholder="your@email.com"
                        required
                        data-testid="guest-email"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                        <input
                          type="text"
                          value={guestInfo.name}
                          onChange={(e) => setGuestInfo({ ...guestInfo, name: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] transition-colors"
                          placeholder="Your name"
                          required
                          data-testid="guest-name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                        <input
                          type="tel"
                          value={guestInfo.phone}
                          onChange={(e) => setGuestInfo({ ...guestInfo, phone: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] transition-colors"
                          placeholder="Your phone number"
                          required
                          data-testid="guest-phone"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <h2 className="font-serif text-2xl font-semibold text-[#1a3a5c] mb-4">Shipping Address</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                  <input
                    type="text"
                    value={address.name}
                    onChange={(e) => setAddress({ ...address, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] transition-colors"
                    required
                    data-testid="address-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    value={address.phone}
                    onChange={(e) => setAddress({ ...address, phone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] transition-colors"
                    required
                    data-testid="address-phone"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Street Address</label>
                <input
                  type="text"
                  value={address.street}
                  onChange={(e) => setAddress({ ...address, street: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] transition-colors"
                  required
                  data-testid="address-street"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                  <input
                    type="text"
                    value={address.city}
                    onChange={(e) => setAddress({ ...address, city: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] transition-colors"
                    required
                    data-testid="address-city"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                  <input
                    type="text"
                    value={address.state}
                    onChange={(e) => setAddress({ ...address, state: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] transition-colors"
                    required
                    data-testid="address-state"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pincode</label>
                  <input
                    type="text"
                    value={address.pincode}
                    onChange={(e) => setAddress({ ...address, pincode: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] transition-colors"
                    required
                    data-testid="address-pincode"
                  />
                </div>
              </div>

              {/* Billing Address Section */}
              <div className="border-t border-gray-200 pt-6 mt-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-serif text-2xl font-semibold text-[#1a3a5c]">Billing Address</h2>
                </div>
                
                <label className="flex items-center space-x-3 mb-4 cursor-pointer">
                  <button
                    type="button"
                    onClick={() => setBillingSameAsShipping(!billingSameAsShipping)}
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      billingSameAsShipping 
                        ? 'bg-[#1a3a5c] border-[#1a3a5c]' 
                        : 'border-gray-300 hover:border-[#1a3a5c]'
                    }`}
                    data-testid="billing-same-checkbox"
                  >
                    {billingSameAsShipping && <Check className="h-3 w-3 text-white" />}
                  </button>
                  <span className="text-gray-700">Same as shipping address</span>
                </label>

                {!billingSameAsShipping && (
                  <div className="space-y-4 mt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                        <input
                          type="text"
                          value={billingAddress.name}
                          onChange={(e) => setBillingAddress({ ...billingAddress, name: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] transition-colors"
                          required={!billingSameAsShipping}
                          data-testid="billing-name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                        <input
                          type="tel"
                          value={billingAddress.phone}
                          onChange={(e) => setBillingAddress({ ...billingAddress, phone: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] transition-colors"
                          required={!billingSameAsShipping}
                          data-testid="billing-phone"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Street Address</label>
                      <input
                        type="text"
                        value={billingAddress.street}
                        onChange={(e) => setBillingAddress({ ...billingAddress, street: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] transition-colors"
                        required={!billingSameAsShipping}
                        data-testid="billing-street"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                        <input
                          type="text"
                          value={billingAddress.city}
                          onChange={(e) => setBillingAddress({ ...billingAddress, city: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] transition-colors"
                          required={!billingSameAsShipping}
                          data-testid="billing-city"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                        <input
                          type="text"
                          value={billingAddress.state}
                          onChange={(e) => setBillingAddress({ ...billingAddress, state: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] transition-colors"
                          required={!billingSameAsShipping}
                          data-testid="billing-state"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Pincode</label>
                        <input
                          type="text"
                          value={billingAddress.pincode}
                          onChange={(e) => setBillingAddress({ ...billingAddress, pincode: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] transition-colors"
                          required={!billingSameAsShipping}
                          data-testid="billing-pincode"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={processingPayment}
                className="w-full bg-[#1a3a5c] text-white py-4 rounded-sm font-serif text-lg tracking-wide hover:bg-[#1f4268] transition-all duration-300 btn-primary mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="place-order-button"
              >
                {processingPayment ? 'Processing...' : `Place Order - ₹${getFinalTotal().toFixed(2)}`}
              </button>
              
              {/* Terms and Conditions Link */}
              <p className="text-xs text-gray-500 text-center mt-3">
                By placing this order, you agree to our{' '}
                <Link to="/policies/terms_conditions" className="text-[#1a3a5c] hover:underline" target="_blank">
                  Terms & Conditions
                </Link>
                ,{' '}
                <Link to="/policies/privacy_policy" className="text-[#1a3a5c] hover:underline" target="_blank">
                  Privacy Policy
                </Link>
                {' '}and{' '}
                <Link to="/policies/refund_policy" className="text-[#1a3a5c] hover:underline" target="_blank">
                  Refund Policy
                </Link>
              </p>
            </form>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:sticky lg:top-24 h-fit space-y-4"
          >
            {/* Promo Code Section */}
            <div className="bg-white p-4 md:p-6 rounded-lg border border-gray-200 overflow-hidden">
              <h3 className="font-serif text-base md:text-lg font-semibold text-[#1a3a5c] mb-4 flex items-center">
                <Tag className="h-5 w-5 mr-2 flex-shrink-0" />
                Promo Code
              </h3>
              {appliedPromo ? (
                <div className="flex items-center justify-between bg-green-50 p-3 rounded-lg">
                  <div className="min-w-0 flex-1">
                    <p className="font-mono font-bold text-green-800 truncate">{appliedPromo.code}</p>
                    <p className="text-sm text-green-600">-₹{promoDiscount.toFixed(2)} applied</p>
                  </div>
                  <button
                    onClick={removePromoCode}
                    className="text-red-500 hover:text-red-700 flex-shrink-0 ml-2"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    placeholder="Enter code"
                    className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] uppercase text-sm"
                    data-testid="promo-code-input"
                  />
                  <button
                    type="button"
                    onClick={applyPromoCode}
                    className="px-3 py-2 bg-[#C9A967] text-white rounded-sm hover:bg-[#B8985A] flex-shrink-0 text-sm whitespace-nowrap"
                    data-testid="apply-promo-btn"
                  >
                    Apply
                  </button>
                </div>
              )}
            </div>

            {/* Reward Points Section - Only for logged in users */}
            {!isGuest && rewardPoints.settings?.is_enabled && rewardPoints.points >= (rewardPoints.settings?.min_points_to_redeem || 100) && (
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="font-serif text-lg font-semibold text-[#1a3a5c] mb-4 flex items-center">
                  <Gift className="h-5 w-5 mr-2" />
                  Reward Points
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  Available: <span className="font-bold text-[#C9A967]">{rewardPoints.points} pts</span>
                  <span className="text-xs ml-1">(= ₹{(rewardPoints.points * (rewardPoints.settings?.points_value_in_rupees || 0.25)).toFixed(2)})</span>
                </p>
                <div className="flex items-center space-x-2">
                  <input
                    type="range"
                    min="0"
                    max={rewardPoints.points}
                    step={rewardPoints.settings?.min_points_to_redeem || 100}
                    value={pointsToUse}
                    onChange={(e) => handlePointsChange(parseInt(e.target.value))}
                    className="flex-1"
                    data-testid="points-slider"
                  />
                  <span className="text-sm font-bold w-16 text-right">{pointsToUse} pts</span>
                </div>
                {pointsDiscount > 0 && (
                  <p className="text-sm text-green-600 mt-2">
                    <Check className="h-4 w-4 inline mr-1" />
                    Saving ₹{pointsDiscount.toFixed(2)}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  Min {rewardPoints.settings?.min_points_to_redeem || 100} pts to redeem. Max {rewardPoints.settings?.max_redeem_percent || 10}% of order.
                </p>
              </div>
            )}

            {/* Order Summary */}
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h3 className="font-serif text-xl font-semibold text-[#1a3a5c] mb-4">Order Summary</h3>
              <div className="space-y-3">
                {cart.map((item) => (
                  <div key={item.product.id} className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {item.product.name} x{item.quantity}
                    </span>
                    <span className="font-medium">
                      ₹{(parseFloat(item.price.totalPrice) * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
                
                <div className="border-t border-gray-200 pt-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span>₹{getTotal().toFixed(2)}</span>
                  </div>
                  
                  {promoDiscount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Promo ({appliedPromo?.code})</span>
                      <span>-₹{promoDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  
                  {pointsDiscount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Points ({pointsToUse} pts)</span>
                      <span>-₹{pointsDiscount.toFixed(2)}</span>
                    </div>
                  )}
                </div>
                
                <div className="border-t border-gray-200 pt-3 flex justify-between font-bold text-lg">
                  <span className="text-[#1a3a5c]">Total</span>
                  <span className="text-[#1a3a5c]" data-testid="order-total">₹{getFinalTotal().toFixed(2)}</span>
                </div>
                
                {rewardPoints.settings?.is_enabled && (
                  <p className="text-xs text-[#C9A967]">
                    You'll earn ~{Math.floor(getFinalTotal() * (rewardPoints.settings?.points_per_rupee || 1))} reward points
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}